import React, { useState } from 'react';
import axios from 'axios';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';

const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal } = useCart();
  const { toast } = useToast();
  const freeShipTarget = 250;
  const remaining = Math.max(0, freeShipTarget - subtotal);
  const progress = Math.min(100, (subtotal / freeShipTarget) * 100);

  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`, {
        items: items.map((i) => ({ id: i.id, qty: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast({ title: 'Checkout unavailable', description: e.response?.data?.detail || 'Please try again shortly.', variant: 'destructive' });
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsOpen(false)}
      />
      <aside
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-[70] shadow-2xl flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-[#141414] text-white">
          <h3 className="font-head text-xl uppercase tracking-wide flex items-center gap-2"><ShoppingBag size={20} /> Your Cart</h3>
          <button onClick={() => setIsOpen(false)} className="hover:text-[#c8102e] transition-colors"><X size={24} /></button>
        </div>

        {items.length > 0 && (
          <div className="px-6 py-3 bg-[#f4f6fa] border-b border-gray-100">
            <p className="text-xs text-gray-600 mb-1.5">
              {remaining > 0 ? <>You're <span className="font-semibold text-[#141414]">${remaining.toFixed(2)}</span> away from free shipping</> : <span className="font-semibold text-green-600">You've unlocked free shipping!</span>}
            </p>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-[#c8102e] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400">
              <ShoppingBag size={48} className="mb-4 opacity-40" />
              <p className="font-cond text-lg">Your cart is currently empty.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-50">
                  <img src={item.image} alt={item.name} className="w-20 h-20 object-contain rounded bg-[#f4f6fa] p-1" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-cond font-semibold text-[#141414] text-sm leading-tight">{item.name}</h4>
                    <p className="text-[#c8102e] font-bold mt-1">${item.price.toFixed(2)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQty(item.id, item.qty - 1)} className="w-7 h-7 border border-gray-200 rounded flex items-center justify-center hover:border-[#c8102e] transition-colors"><Minus size={13} /></button>
                      <span className="w-8 text-center font-semibold">{item.qty}</span>
                      <button onClick={() => updateQty(item.id, item.qty + 1)} className="w-7 h-7 border border-gray-200 rounded flex items-center justify-center hover:border-[#c8102e] transition-colors"><Plus size={13} /></button>
                      <button onClick={() => removeItem(item.id)} className="ml-auto text-gray-400 hover:text-[#c8102e] transition-colors"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-gray-100 bg-white">
            <div className="flex items-center justify-between mb-4">
              <span className="font-cond uppercase text-gray-500 tracking-wide">Subtotal</span>
              <span className="font-head text-2xl text-[#141414]">${subtotal.toFixed(2)}</span>
            </div>
            <button data-testid="cart-checkout-button" onClick={handleCheckout} disabled={loading} className="btn-crimson w-full text-base disabled:opacity-60">
              {loading ? 'Redirecting to secure checkout…' : 'Checkout'}
            </button>
            <p className="text-center text-xs text-gray-400 mt-2">Secure payment powered by Stripe</p>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
